import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBook, FiCheckSquare, FiClock, FiShield, FiGlobe, FiMail, FiBarChart, FiLink, FiCheckCircle } = FiIcons;

const SOPLibrary = () => {
  const [activeTab, setActiveTab] = useState('web');

  const sops = {
    web: {
      title: 'Web Page Updates',
      icon: FiGlobe,
      steps: ['Receive request via form', 'Duplicate existing page template', 'Update copy/images', 'Check mobile responsiveness', 'Verify SEO tags', 'Submit for staging review', 'Publish & Purge Cache']
    },
    publish: {
      title: 'Content Publishing',
      icon: FiBook,
      steps: ['Format text in CMS', 'Add internal links (min 3)', 'Set featured image (alt text required)', 'Set URL slug (clean)', 'Schedule publish time', 'Verify on live site']
    },
    campaign: {
      title: 'Campaign Launch Checklist',
      icon: FiCheckCircle,
      steps: ['Confirm all assets approved', 'Test all landing page forms', 'Verify tracking pixels firing', 'Check email automation triggers', 'Coordinate social posts', 'Notify support team']
    },
    email: {
      title: 'Email Marketing Workflow',
      icon: FiMail,
      steps: ['Duplicate template', 'Insert copy/images', 'Send test to self', 'Check links on mobile', 'Verify subject line/preheader', 'Send to manager for approval', 'Schedule']
    },
    seo: {
      title: 'SEO Quick Checklist',
      icon: FiGlobe,
      steps: ['Keyphrase in H1?', 'Meta description < 160 chars?', 'Images compressed?', 'URL structure clean?', 'Inbound links added?']
    },
    utm: {
      title: 'UTM Builder Instructions',
      icon: FiLink,
      steps: ['Open UTM builder tool', 'Source = e.g., newsletter', 'Medium = e.g., email', 'Campaign = e.g., spring_sale', 'Content = e.g., hero_button', 'Copy & Test link']
    },
    qa: {
      title: 'QA Workflow',
      icon: FiShield,
      steps: ['Check on Desktop (Chrome/Safari)', 'Check on Mobile (iOS/Android)', 'Test all buttons/links', 'Submit test form entry', 'Verify Thank You page', 'Check email receipt']
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">SOP Library: The Coordinator Playbook</h1>
        <p className="text-lg text-gray-600">
          The single source of truth. Every task has a defined standard to ensure quality and speed.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Navigation */}
        <div className="lg:col-span-1 space-y-2">
          {Object.entries(sops).map(([key, data]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`w-full flex items-center space-x-3 p-4 rounded-xl text-left transition-all ${
                activeTab === key
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-emerald-50'
              }`}
            >
              <SafeIcon icon={data.icon} className="w-5 h-5" />
              <span className="font-medium">{data.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="lg:col-span-2">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="glass-card rounded-xl p-8 min-h-[500px]"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mr-4">
                <SafeIcon icon={sops[activeTab].icon} className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{sops[activeTab].title}</h2>
                <p className="text-sm text-gray-500">Standard Operating Procedure</p>
              </div>
            </div>

            <div className="space-y-4">
              {sops[activeTab].steps.map((step, idx) => (
                <div key={idx} className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 mr-4">
                    {idx + 1}
                  </div>
                  <p className="text-gray-700 font-medium">{step}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500">
                <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                Estimated time: 15-30 mins
              </div>
              <button className="text-emerald-600 font-medium text-sm hover:text-emerald-700">
                Download PDF Checklist &rarr;
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SOPLibrary;